import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {Meta, MetaDefinition, Title} from '@angular/platform-browser';

import {MetaTag} from '../types/MetaTag';
import {MetaTagToOgTagMapItem} from '../types/MetaTagToOgTagMapItem';
import {MetaTagFieldsEnum, MetaTypesEnum} from '../enums/SeoEnums';
import {ToolsService} from './tools.service';
import {isPlatformBrowser} from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class SeoMetaService {
  private tagUpdatersMap: Array<Function> = [];
  private metaTags: MetaTag[];
  private tagFieldToOgFieldMap: MetaTagToOgTagMapItem[] = [];

  constructor(
    private metaService: Meta,
    private titleService: Title,
    private tools: ToolsService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.createTagFieldToOgFieldMap();
    this.createTagUpdatersMap();
  }

  updateMetaTags(metaTags: MetaTag[]): void {
    if (metaTags && metaTags.length > 0) {
      this.metaTags = metaTags;
      metaTags.forEach((metaTag) => {
        this.conditionalTagUpdate(metaTag);
      });
    }
  }

  addMetaTag(metaTag: MetaTag): void {
    this.conditionalTagUpdate(metaTag);
  }

  private conditionalTagUpdate(metaTag: MetaTag): void {
    if (!this.tools.isNil(this.tagUpdatersMap[metaTag.type])) {
      this.tagUpdatersMap[metaTag.type](metaTag);
      this.conditionalOgTagUpdate(metaTag);
    }
  }

  private conditionalOgTagUpdate(metaTag: MetaTag): void {
    if (!this.tools.isNil(this.tagFieldToOgFieldMap[metaTag.field])) {
      this.addMissingOgTagBasedOnExistingMetaTag(metaTag, this.tagFieldToOgFieldMap[metaTag.field]);
    }
  }

  private createTagUpdatersMap(): void {
    this.tagUpdatersMap[MetaTypesEnum.TITLE] = (metaTag: MetaTag) => this.updateTitle(metaTag);
    this.tagUpdatersMap[MetaTypesEnum.NAME] = (metaTag: MetaTag) => this.updateNameTag(metaTag);
    this.tagUpdatersMap[MetaTypesEnum.PROPERTY] = (metaTag: MetaTag) => this.updatePropertyTag(metaTag);
  }

  private createTagFieldToOgFieldMap(): void {
    this.tagFieldToOgFieldMap[MetaTagFieldsEnum.TITLE] = MetaTagFieldsEnum.OGTITLE;
    this.tagFieldToOgFieldMap[MetaTagFieldsEnum.DESCRIPTION] = MetaTagFieldsEnum.OGDESCRIPTION;
  }

  private updateTitle(metaTag: MetaTag): void {
    this.titleService.setTitle(metaTag.value);
  }

  private updateNameTag(metaTag: MetaTag): void {
    let metaEl: HTMLMetaElement = null;
    if (isPlatformBrowser(this.platformId)) {
      metaEl = this.metaService.getTag('name = "' + metaTag.field + '"');
    }

    const tag: MetaDefinition = {name: metaTag.field, content: metaTag.value};
    this.addOrUpdateMetaTag(tag, metaEl);
  }

  private updatePropertyTag(metaTag: MetaTag): void {
    let metaEl: HTMLMetaElement = null;
    if (isPlatformBrowser(this.platformId)) {
      metaEl = this.metaService.getTag('property = "' + metaTag.field + '"');
    }

    const tag: MetaDefinition = {
      property: metaTag.field,
      content: metaTag.value,
    };
    this.addOrUpdateMetaTag(tag, metaEl);
  }

  private addOrUpdateMetaTag(tag: MetaDefinition, metaEl: HTMLMetaElement | null): void {
    if (this.tools.isNil(metaEl)) {
      this.metaService.addTag(tag);
    } else {
      this.metaService.updateTag(tag);
    }
  }

  private isTagExists(field: string): boolean {
    return !this.tools.isNil(this.metaTags.find((tag) => tag.field === field));
  }

  private addMissingOgTagBasedOnExistingMetaTag(metaTag: MetaTag, ogProperty: string): void {
    if (!this.isTagExists(ogProperty)) {
      const ogMetaTag: MetaTag = this.tools.clone(metaTag);
      ogMetaTag.field = ogProperty;
      ogMetaTag.type = MetaTypesEnum.PROPERTY;
      this.updatePropertyTag(ogMetaTag);
    }
  }
}
